import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthContext } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/lib/supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  adminOnly?: boolean;
}

const allNavItems: NavItem[] = [
  { label: 'Dashboard',     path: '/dashboard',     icon: 'ri-dashboard-line' },
  { label: 'Products',      path: '/products',       icon: 'ri-box-3-line' },
  { label: 'Orders',        path: '/orders',         icon: 'ri-shopping-cart-line' },
  { label: 'Payments',      path: '/payments',       icon: 'ri-secure-payment-line' },
  { label: 'Subscriptions', path: '/subscriptions',  icon: 'ri-key-2-line' },
  { label: 'Developer',     path: '/developer',      icon: 'ri-terminal-line' },
  { label: 'Support',       path: '/support',        icon: 'ri-customer-service-2-line' },
  { label: 'My Account',    path: '/account',        icon: 'ri-user-settings-line' },
  { label: 'Admin CMS',     path: '/admin',          icon: 'ri-settings-4-line', adminOnly: true },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuthContext();
  const { profile } = useProfile();

  // ── Real-time new-order badge ───────────────────────────────────────────
  const [newOrderCount, setNewOrderCount] = useState(0);
  const isOnOrdersPage = location.pathname === '/orders';
  const prevPathRef = useRef(location.pathname);

  // Clear badge when user navigates to orders page
  useEffect(() => {
    if (location.pathname === '/orders' && prevPathRef.current !== '/orders') {
      setNewOrderCount(0);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Subscribe to realtime INSERT on orders
  useEffect(() => {
    const channel = supabase
      .channel('sidebar-orders-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        () => {
          // Only increment if not currently on orders page
          if (window.location.pathname !== '/orders') {
            setNewOrderCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const { settings: siteSettings } = useSiteSettings();
  const siteLogo = siteSettings.site_logo_url ?? '';
  const siteName = siteSettings.site_name || 'API MART';

  const navItems = allNavItems.filter(item => !item.adminOnly || user?.role === 'admin');
  const avatarUrl = profile?.avatar_url ?? null;
  const initials = (user?.username ?? 'U').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <motion.aside
      animate={{ width: collapsed ? 72 : 240 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="flex flex-col h-screen bg-sidebar text-white overflow-hidden shrink-0 z-20"
    >
      {/* Logo — click goes to dashboard */}
      <div
        className={`flex items-center border-b border-white/10 min-h-[72px] cursor-pointer ${collapsed ? 'justify-center px-4 py-5 gap-3' : 'justify-center px-4 py-4 gap-3'}`}
        onClick={() => navigate('/dashboard')}
        title="Go to Dashboard"
      >
        {siteLogo ? (
          collapsed ? (
            <img
              src={siteLogo}
              alt={siteName}
              className="w-9 h-9 rounded-lg object-contain shrink-0 mx-auto"
            />
          ) : (
            <img
              src={siteLogo}
              alt={siteName}
              className="w-full h-10 object-contain rounded-lg"
            />
          )
        ) : (
          <>
            <div className="w-9 h-9 rounded-lg bg-brand-500 flex items-center justify-center shrink-0">
              <i className="ri-code-box-line text-white text-lg" />
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <div className="font-bold text-base tracking-wide whitespace-nowrap">{siteName}</div>
                  <div className="text-[10px] text-white/40 font-mono whitespace-nowrap">ONE API. INFINITE TXN.</div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 flex flex-col gap-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const isAdmin = item.adminOnly;
          const showBadge = item.path === '/orders' && newOrderCount > 0 && !isOnOrdersPage;

          return (
            <div key={item.path}>
              {isAdmin && <div className="mx-2 my-2 border-t border-white/10" />}
              <button
                onClick={() => {
                  if (item.path === '/orders') setNewOrderCount(0);
                  navigate(item.path);
                }}
                className={`sidebar-item w-full text-left transition-all ${
                  isActive
                    ? 'bg-brand-500 text-white'
                    : isAdmin
                    ? 'text-brand-300/70 hover:text-brand-200 hover:bg-brand-500/20'
                    : 'text-white/60 hover:text-white hover:bg-sidebar-hover'
                }`}
                title={collapsed ? item.label : undefined}
              >
                {/* Icon + badge wrapper */}
                <span className="w-5 h-5 flex items-center justify-center shrink-0 relative">
                  <i className={`${item.icon} text-lg`} />
                  {showBadge && collapsed && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {newOrderCount > 9 ? '9+' : newOrderCount}
                    </span>
                  )}
                </span>

                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                      className="whitespace-nowrap overflow-hidden flex-1"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Badge when expanded */}
                {!collapsed && showBadge && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0"
                  >
                    {newOrderCount > 9 ? '9+' : newOrderCount}
                  </motion.span>
                )}

                {!collapsed && isAdmin && !showBadge && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-[9px] bg-brand-500/30 text-brand-300 px-1.5 py-0.5 rounded-full font-bold shrink-0"
                  >
                    ADMIN
                  </motion.span>
                )}
              </button>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 p-2 space-y-1">
        <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-brand-500/30 border border-brand-400/30 flex items-center justify-center shrink-0 overflow-hidden">
            {avatarUrl ? (
              <img src={avatarUrl} alt={user?.username ?? 'User'} className="w-full h-full object-cover" />
            ) : (
              <span className="text-brand-300 text-xs font-bold">{initials}</span>
            )}
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="overflow-hidden min-w-0"
              >
                <div className="text-xs font-semibold text-white/90 truncate">{user?.username ?? 'Guest'}</div>
                <div className="text-[10px] text-white/40 truncate">{user?.email ?? ''}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="sidebar-item w-full text-white/50 hover:text-white hover:bg-sidebar-hover"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-lg`} />
          </span>
          <AnimatePresence>
            {!collapsed && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="whitespace-nowrap">
                Collapse
              </motion.span>
            )}
          </AnimatePresence>
        </button>

        <button
          onClick={handleLogout}
          className="sidebar-item w-full text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title={collapsed ? 'Logout' : undefined}
        >
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-r-line text-lg" />
          </span>
          <AnimatePresence>
            {!collapsed && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="whitespace-nowrap">
                Logout
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}
